import type { PropsWithChildren } from "react";
import { Route, Switch } from "wouter";

import { useAuth } from "./auth";
import { AppShell } from "./components/app-shell";
import { LoadingState } from "./components/ui";
import {
  AdminAccessPage,
  AdminBuyersPage,
  AdminLicensesPage,
  AdminOverviewPage,
  AdminProductsPage,
} from "./pages/admin-pages";
import {
  AdminAuditWorkspacePage,
  AdminBuiltByBitPage,
  AdminBuyerDetailPage,
  AdminOperationsCenterPage,
} from "./pages/admin-operations-pages";
import { AuthPage } from "./pages/auth-page";
import { AdminDiscordPanelPage } from "./pages/admin-discord-panel-page";
import { AdminTicketPanelPage } from "./pages/admin-ticket-panel-page";
import {
  AddLicensePage,
  LicensesPage,
  NotFoundPage,
  OverviewPage,
} from "./pages/buyer-pages";
import { LicenseDetailPage } from "./pages/license-detail-page";
import { Navigate, usePathname } from "./router";

export function App() {
  return (
    <Switch>
      <Route path="/login"><AuthPage mode="login" /></Route>
      <Route path="/register"><AuthPage mode="register" /></Route>
      <Route><ProtectedWorkspace /></Route>
    </Switch>
  );
}

function ProtectedWorkspace() {
  const { account, loading } = useAuth();
  const pathname = usePathname();
  if (loading) {
    return <div className="route-loader"><LoadingState label="Opening oCloud License" /></div>;
  }
  if (!account) {
    return <Navigate replace to={`/login?next=${encodeURIComponent(pathname)}`} />;
  }
  return (
    <AppShell>
      <Switch>
        <Route path="/"><Navigate replace to="/dashboard" /></Route>
        <Route path="/dashboard"><OverviewPage /></Route>
        <Route path="/licenses"><LicensesPage /></Route>
        <Route path="/licenses/:id"><LicenseDetailPage /></Route>
        <Route path="/add-license"><AddLicensePage /></Route>
        <Route path="/admin"><AdminGuard><AdminOverviewPage /></AdminGuard></Route>
        <Route path="/admin/licenses"><AdminGuard><AdminLicensesPage /></AdminGuard></Route>
        <Route path="/admin/products"><AdminGuard><AdminProductsPage /></AdminGuard></Route>
        <Route path="/admin/buyers/:discordUserId"><AdminGuard><AdminBuyerDetailPage /></AdminGuard></Route>
        <Route path="/admin/buyers"><AdminGuard><AdminBuyersPage /></AdminGuard></Route>
        <Route path="/admin/access"><AdminGuard><AdminAccessPage /></AdminGuard></Route>
        <Route path="/admin/audit"><AdminGuard><AdminAuditWorkspacePage /></AdminGuard></Route>
        <Route path="/admin/builtbybit"><AdminGuard><AdminBuiltByBitPage /></AdminGuard></Route>
        <Route path="/admin/operations"><AdminGuard><AdminOperationsCenterPage /></AdminGuard></Route>
        <Route path="/admin/discord-panel"><AdminGuard><AdminDiscordPanelPage /></AdminGuard></Route>
        <Route path="/admin/ticket-panel"><AdminGuard><AdminTicketPanelPage /></AdminGuard></Route>
        <Route><NotFoundPage /></Route>
      </Switch>
    </AppShell>
  );
}

function AdminGuard({ children }: PropsWithChildren) {
  const { account } = useAuth();
  return account?.isAdmin ? children : <Navigate replace to="/dashboard" />;
}
